import { LOGO_UPLOAD_EXTENSIONS } from '@cutr/constants/files';
import cn from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router-dom';
import { toast } from 'sonner';

import { ApiError } from '@/api/backend';
import { Button } from '@/primitives/Button';
import { Icon, Loader } from '@/primitives/Icons';
import { Upload } from '@/primitives/Icons';

import { WindowSettingsLoaderType } from '../queries/order/loaders';
import { windowApi } from '../queries/windowApi';
import styles from './ResellerSettings.module.css';

export const ResellerSettings = () => {
  const { logoUrl } = useLoaderData() as WindowSettingsLoaderType;

  const { t } = useTranslation();
  const [isUploading, setIsUploading] = React.useState(false);
  const [selectedFile, setSelectedFile] = React.useState<File>();
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(
    logoUrl || null
  );
  const [error, setError] = React.useState<string>('');
  const logoId = React.useId();

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setSelectedFile(file);

      await handleFileUpload(file);
    }
  };

  const handleFileUpload = async (file: File) => {
    setIsUploading(true);
    try {
      await windowApi.updateOrderLogo(file);
      setError('');
      toast.success(t('reseller.settings.logoForm.uploadSuccess'));
    } catch (error) {
      console.error('Failed to upload logo:', error);
      const message =
        error instanceof ApiError && error.response.status === 422
          ? t('reseller.settings.logoForm.unsupportedFileType', {
              types: LOGO_UPLOAD_EXTENSIONS.join(', '),
            })
          : t('reseller.settings.logoForm.uploadError', {
              error: (error as Error).message,
            });
      setError(message);
      toast.error(message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteLogo = async () => {
    await windowApi.deleteOrderLogo();
    toast.success(t('reseller.settings.logoForm.removeSuccess'));
    setSelectedFile(undefined);
    setPreviewUrl(null);
    setError('');
  };

  return (
    <main className={styles.content}>
      <div className={styles.logoCard}>
        <h2>{t('reseller.settings.logoForm.title')}</h2>
        <p>{t('reseller.settings.logoForm.description')}</p>
        <p></p>
        <div className={styles.logoSection}>
          {previewUrl && (
            <LogoPreview
              selectedFile={selectedFile}
              previewUrl={previewUrl}
              error={error}
              isUploading={isUploading}
            >
              <div className="flexAlign">
                <Button
                  variant="delete"
                  onClick={handleDeleteLogo}
                  disabled={isUploading}
                >
                  {t('reseller.settings.logoForm.remove')}
                </Button>
                <Button
                  className="full transparent outline"
                  onClick={() => {
                    const input = document.getElementById(
                      logoId
                    ) as HTMLInputElement;
                    input.click();
                  }}
                  icon={<Icon icon={<Upload />} />}
                  disabled={isUploading}
                >
                  {t('reseller.settings.logoForm.upload')}
                </Button>
              </div>
              <input
                type="file"
                id={logoId}
                accept={LOGO_UPLOAD_EXTENSIONS.join(',')}
                className="hidden"
                onChange={handleFileSelect}
              />
            </LogoPreview>
          )}
          {!selectedFile && !previewUrl && (
            <LogoUpload
              handleFileSelect={handleFileSelect}
              disabled={isUploading}
            />
          )}
        </div>
      </div>
    </main>
  );
};

const LogoPreview = ({
  selectedFile,
  previewUrl,
  error,
  isUploading,
  children,
}: {
  selectedFile?: File;
  previewUrl: string;
  error: string;
  isUploading: boolean;
  children: React.ReactNode;
}) => {
  const fileSize = selectedFile
    ? (selectedFile.size / 1024 / 1024).toFixed(2)
    : '';
  const fileDetails = selectedFile
    ? `${selectedFile.name} (${fileSize} MB)`
    : '';

  const getStatus = () => {
    if (isUploading) {
      return <Icon icon={<Loader />} style={{ margin: '0 auto' }} />;
    }
    if (error) {
      return (
        <strong className={cn(styles.fileInfo, styles.error)}>{error}</strong>
      );
    }
    return <br />;
  };

  return (
    <div className={styles.logoPreview}>
      <img src={previewUrl!} alt={fileDetails} className={styles.logoImage} />
      <div className="stack gap-xs">
        <p className={styles.fileInfo}>{fileDetails}</p>
        {getStatus()}
      </div>
      {children}
    </div>
  );
};

const LogoUpload = ({
  handleFileSelect,
  disabled,
}: {
  handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disabled: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <div className={styles.uploadArea}>
      <input
        type="file"
        id="logoUpload"
        accept={LOGO_UPLOAD_EXTENSIONS.join(',')}
        onChange={handleFileSelect}
        className={styles.fileInput}
        disabled={disabled}
      />
      <label htmlFor="logoUpload" className={styles.uploadLabel}>
        <Icon icon={<Upload />} />
        <span>{t('reseller.settings.logoForm.upload')}</span>
      </label>
      <p className={styles.ratioHint}>
        {t('reseller.settings.logoForm.maxSize')}
      </p>
    </div>
  );
};
